import { createContext, PropsWithChildren, ReactNode, useContext } from 'react';

/**
 * `grid` stacks each label over its value in columns; `inline` puts the label on the left and the value
 * right-aligned beside it, one fact per line, for inspectors and tooltips.
 */
export type DescriptionListLayout = 'grid' | 'inline';

/** `inherit` takes the surrounding text color, for lists inside tooltips and other inverse surfaces. */
export type DescriptionListTone = 'default' | 'inherit';

export type DescriptionListProps = PropsWithChildren<{
  className?: string;
  compact?: boolean;
  /** Container breakpoints for narrow embedded panels on a wide screen. */
  responsive?: 'viewport' | 'container';
  layout?: DescriptionListLayout;
  tone?: DescriptionListTone;
}>;

const ListContext = createContext<{ layout: DescriptionListLayout; tone: DescriptionListTone }>({
  layout: 'grid',
  tone: 'default',
});

const gridClassName = (responsive: 'viewport' | 'container', compact: boolean) =>
  [
    responsive === 'container'
      ? 'grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3'
      : 'grid-cols-2 lg:grid-cols-3',
    compact ? 'gap-x-4 gap-y-3' : 'gap-x-7 gap-y-7',
  ].join(' ');

export const DescriptionList = ({
  className,
  compact = false,
  responsive = 'viewport',
  layout = 'grid',
  tone = 'default',
  children,
}: DescriptionListProps) => (
  <ListContext.Provider value={{ layout, tone }}>
    <dl
      className={[
        'grid',
        layout === 'inline'
          ? `grid-cols-[auto_1fr] gap-x-3 tabular-nums ${compact ? '' : 'gap-y-1 text-sm'}`
          : gridClassName(responsive, compact),
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </dl>
  </ListContext.Provider>
);

export const DescriptionItem = ({
  label,
  children,
}: {
  label: ReactNode;
  children?: ReactNode;
}) => {
  const { layout, tone } = useContext(ListContext);
  const value = children === 0 ? 0 : children || '—';
  if (layout === 'inline') {
    const inherit = tone === 'inherit';
    return (
      <div className="contents">
        <dt className={inherit ? 'opacity-70' : 'text-[var(--ots-text-muted)]'}>{label}</dt>
        <dd className={`min-w-0 break-words text-right ${inherit ? 'font-semibold' : 'font-medium'}`}>{value}</dd>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-[var(--ots-text-muted)]">
        {label}
      </dt>
      <dd className="mt-1.5 break-words text-sm font-semibold text-[var(--ots-secondary)]">
        {value}
      </dd>
    </div>
  );
};
